"use client";

import React from "react";
import { ClipboardCheck, Target, MessageSquareText, Route } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const features = [{
  title: "Annual Reviews",
  description: "Structured reviews help recognize strong work, discuss challenges, and identify practical areas for growth.",
  icon: ClipboardCheck
}, {
  title: "Goal Setting",
  description: "Clear goals help connect your daily work with client needs, firm priorities, and your own professional development.",
  icon: Target
}, {
  title: "Ongoing Feedback",
  description: "Regular check-ins and direct communication help you understand what is working, what needs attention, and where support is needed.",
  icon: MessageSquareText
}, {
  title: "A Clear Path Forward",
  description: "As your skills and judgment grow, we look for opportunities to increase responsibility, ownership, and client-facing work.",
  icon: Route
}];
export default function WhyTrustAventuraGrid() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="260512" id="clear-expectations-and-ongoing-feedback" className="bg-primary-900 text-white py-16 md:py-24 border-b border-white/10">
      <div ref={ref} className={cn("max-w-7xl mx-auto px-6 lg:px-8 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <div className="h-1 w-12 bg-secondary-400 mx-auto mb-6 rounded-full" />

          <p className="text-secondary-300 font-semibold tracking-[0.18em] text-xs uppercase mb-4 font-default">
            Expectations and Feedback
          </p>

          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight tracking-tight">
            Clear expectations and ongoing feedback.
          </h2>

          <p className="text-primary-100/80 text-lg md:text-xl max-w-2xl mx-auto font-default leading-relaxed">
            People do their best work when they understand what is expected,
            where they are heading, and how they can keep improving. We aim to
            make feedback practical, direct, and useful.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <div key={i} data-index={i} className="bg-white/7 border border-white/10 rounded-2xl p-6 md:p-7 flex flex-col h-full shadow-sm hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-secondary-400/15 flex items-center justify-center mb-5">
                  <Icon className="size-6 text-secondary-300" />
                </div>

                <h3 className="font-default text-xl font-semibold text-white mb-3 leading-snug">
                  {feature.title}
                </h3>

                <p className="text-primary-100/75 text-sm md:text-base leading-relaxed font-default">
                  {feature.description}
                </p>
              </div>;
        })}
        </div>

        <div className="max-w-4xl mx-auto mt-12 md:mt-16 rounded-3xl border border-white/10 bg-white/7 p-6 md:p-8 text-center">
          <p className="font-default text-lg md:text-xl text-white font-semibold leading-relaxed">
            The goal is simple: people should not have to guess where they
            stand.
          </p>

          <p className="font-default text-primary-100/75 leading-relaxed mt-4">
            Clear expectations, regular feedback, and practical support help
            build confidence, improve the work, and create room for people to
            take on more responsibility over time.
          </p>
        </div>
      </div>
    </section>;
}